import React, { useState, useEffect } from 'react';
import { Chart as ChartJS, ArcElement, Tooltip, Legend } from 'chart.js';
import { Pie } from 'react-chartjs-2';
import { FaInfoCircle } from 'react-icons/fa';
import { Helmet } from 'react-helmet';

// Register the required elements
ChartJS.register(ArcElement, Tooltip, Legend);

const DogFoodCalculator = () => {
  const [weight, setWeight] = useState('');
  const [age, setAge] = useState('');
  const [activity, setActivity] = useState('');
  const [result, setResult] = useState(null);

  const calculatePortion = () => {
    if (!weight || !age || !activity) {
      alert('Please fill in all fields');
      return;
    }

    const weightNum = parseFloat(weight);
    let baseCalories = weightNum * 30 + 70; // Basic calculation for maintenance

    // Adjust based on age
    const ageFactors = {
      puppy: 1.5,
      adult: 1.2,
      senior: 0.9
    };

    // Adjust based on activity level
    const activityFactors = {
      low: 0.8,
      moderate: 1.0,
      high: 1.2
    };

    const totalCalories = baseCalories * ageFactors[age] * activityFactors[activity];
    const dailyPortion = (totalCalories / 3500) * 1000; // Approximate conversion

    // Calculate macronutrient distribution
    const proteinGrams = dailyPortion * 0.25; // 25% protein
    const fatGrams = dailyPortion * 0.20; // 20% fat
    const carbsGrams = dailyPortion * 0.55; // 55% carbs

    setResult({
      calories: Math.round(totalCalories),
      portion: Math.round(dailyPortion),
      protein: proteinGrams,
      fat: fatGrams,
      carbs: carbsGrams
    });
  };

  const chartData = result ? {
    labels: ['Protein', 'Fat', 'Carbs'],
    datasets: [
      {
        data: [result.protein, result.fat, result.carbs],
        backgroundColor: ['#4CAF50', '#2196F3', '#FF9800'],
        hoverBackgroundColor: ['#45A049', '#1976D2', '#F57C00']
      }
    ]
  } : null;

  return (
    <>
      <Helmet>
        <title>Dog Food Calculator - Calculate Proper Dog Food Portions</title>
        <meta name="description" content="Calculate proper dog food portions based on weight, age, and activity level. Get accurate daily calorie needs and nutritional balance for your dog." />
        <meta name="keywords" content="dog food calculator, dog nutrition, pet food calculator, dog calorie calculator, puppy food calculator" />
        
        {/* Open Graph / Facebook */}
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://your-website.com/dog-food-calculator" />
        <meta property="og:title" content="Dog Food Calculator - Calculate Proper Dog Food Portions" />
        <meta property="og:description" content="Calculate proper dog food portions based on weight, age, and activity level. Get accurate daily calorie needs and nutritional balance for your dog." />
        <meta property="og:image" content="https://your-website.com/images/dog-food-calculator-preview.png" />

        {/* Twitter */}
        <meta property="twitter:card" content="summary_large_image" />
        <meta property="twitter:url" content="https://your-website.com/dog-food-calculator" />
        <meta property="twitter:title" content="Dog Food Calculator - Calculate Proper Dog Food Portions" />
        <meta property="twitter:description" content="Calculate proper dog food portions based on weight, age, and activity level. Get accurate daily calorie needs and nutritional balance for your dog." />
        <meta property="twitter:image" content="https://your-website.com/images/dog-food-calculator-preview.png" />
      </Helmet>

      <div className="max-w-5xl mx-auto p-6 bg-white rounded-lg shadow-lg">
        <div className="text-center mb-8">
          <h1 className="text-4xl font-bold text-green-600 mb-2">
            🐶 Dog Food Calculator
          </h1>
          <p className="text-gray-600">
            Calculate proper dog food portions and nutritional balance
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Dog's Weight (kg)
            </label>
            <input
              type="number"
              value={weight}
              onChange={(e) => setWeight(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
              placeholder="Enter dog's weight"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Age
            </label>
            <select
              value={age}
              onChange={(e) => setAge(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
            >
              <option value="">Select age group</option>
              <option value="puppy">Puppy (0-1 year)</option>
              <option value="adult">Adult (1-7 years)</option>
              <option value="senior">Senior (7+ years)</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Activity Level
            </label>
            <select
              value={activity}
              onChange={(e) => setActivity(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
            >
              <option value="">Select activity level</option>
              <option value="low">Low</option>
              <option value="moderate">Moderate</option>
              <option value="high">High</option>
            </select>
          </div>
        </div>

        <button
          onClick={calculatePortion}
          className="w-full bg-green-600 text-white py-2 px-4 rounded-md hover:bg-green-700 transition-colors"
        >
          Calculate Dog Food Portion
        </button>

        {result && (
          <div className="mt-8">
            <h2 className="text-2xl font-bold text-green-600 mb-4">Results</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="bg-gray-50 p-4 rounded-lg">
                <h3 className="text-lg font-semibold mb-2">Daily Recommendations</h3>
                <p className="text-green-600 text-xl font-bold mb-2">
                  {result.calories} calories per day
                </p>
                <p className="text-green-600 text-xl font-bold">
                  {result.portion} grams of food per day
                </p>
              </div>
              <div className="bg-gray-50 p-4 rounded-lg">
                <h3 className="text-lg font-semibold mb-2">Nutritional Breakdown</h3>
                <div className="w-full h-[300px]">
                  <Pie data={chartData} />
                </div>
              </div>
            </div>
          </div>
        )}

        <div className="mt-8 bg-gray-50 p-6 rounded-lg">
          <h2 className="text-xl font-semibold mb-4">FAQs</h2>
          <div className="space-y-4">
            <div className="border-l-4 border-green-600 pl-4">
              <h3 className="font-semibold">What factors affect my dog's food requirements?</h3>
              <p className="text-gray-600">
                Age, weight, and activity level are the primary factors. Puppies need more calories for growth, while seniors require fewer calories.
              </p>
            </div>
            <div className="border-l-4 border-green-600 pl-4">
              <h3 className="font-semibold">How often should I feed my dog?</h3>
              <p className="text-gray-600">
                Generally, adult dogs should be fed twice a day. Puppies may require more frequent feedings.
              </p>
            </div>
            <div className="border-l-4 border-green-600 pl-4">
              <h3 className="font-semibold">Why is nutritional balance important?</h3>
              <p className="text-gray-600">
                Proper balance of protein, fat, and carbohydrates is crucial for your dog's overall health and energy levels.
              </p>
            </div>
          </div>
        </div>

        <div className="mt-8 bg-gray-50 p-4 rounded-lg mb-8">
          <h2 className="text-xl font-semibold mb-4">How to Use</h2>
          <div className="space-y-4">
            <div className="border-l-4 border-green-600 pl-4">
              <h3 className="font-semibold mb-2">Step 1: Enter Your Dog's Information</h3>
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <span className="w-8 h-8 rounded-full bg-green-100 flex items-center justify-center text-green-600 font-bold">1</span>
                  <div>
                    <p className="text-gray-700">Input your dog's weight in kilograms</p>
                    <p className="text-sm text-gray-500">This is crucial for accurate calculations</p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <span className="w-8 h-8 rounded-full bg-green-100 flex items-center justify-center text-green-600 font-bold">2</span>
                  <div>
                    <p className="text-gray-700">Select your dog's age group</p>
                    <p className="text-sm text-gray-500">Choose from Puppy (0-1 year), Adult (1-7 years), or Senior (7+ years)</p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <span className="w-8 h-8 rounded-full bg-green-100 flex items-center justify-center text-green-600 font-bold">3</span>
                  <div>
                    <p className="text-gray-700">Choose your dog's activity level</p>
                    <p className="text-sm text-gray-500">Select Low, Moderate, or High based on your dog's daily activity</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="border-l-4 border-green-600 pl-4">
              <h3 className="font-semibold mb-2">Step 2: Calculate</h3>
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <span className="w-8 h-8 rounded-full bg-green-100 flex items-center justify-center text-green-600 font-bold">1</span>
                  <div>
                    <p className="text-gray-700">Click the "Calculate Dog Food Portion" button</p>
                    <p className="text-sm text-gray-500">The calculator will process your inputs and show results</p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <span className="w-8 h-8 rounded-full bg-green-100 flex items-center justify-center text-green-600 font-bold">2</span>
                  <div>
                    <p className="text-gray-700">Review the results section</p>
                    <p className="text-sm text-gray-500">Check daily calorie needs and food portion recommendations</p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <span className="w-8 h-8 rounded-full bg-green-100 flex items-center justify-center text-green-600 font-bold">3</span>
                  <div>
                    <p className="text-gray-700">Analyze the nutritional breakdown</p>
                    <p className="text-sm text-gray-500">View the pie chart showing protein, fat, and carbohydrate distribution</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="border-l-4 border-green-600 pl-4">
              <h3 className="font-semibold mb-2">Important Notes</h3>
              <div className="space-y-2">
                <div className="bg-green-50 p-3 rounded-lg">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-full bg-green-100 flex items-center justify-center text-green-600">
                      <FaInfoCircle />
                    </div>
                    <div>
                      <p className="text-gray-700">Consult with your veterinarian</p>
                      <p className="text-sm text-gray-500">Always consult your vet before making significant changes to your dog's diet</p>
                    </div>
                  </div>
                </div>
                <div className="bg-green-50 p-3 rounded-lg">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-full bg-green-100 flex items-center justify-center text-green-600">
                      <FaInfoCircle />
                    </div>
                    <div>
                      <p className="text-gray-700">Monitor your dog's weight</p>
                      <p className="text-sm text-gray-500">Regularly check your dog's weight and adjust portions as needed</p>
                    </div>
                  </div>
                </div>
                <div className="bg-green-50 p-3 rounded-lg">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-full bg-green-100 flex items-center justify-center text-green-600">
                      <FaInfoCircle />
                    </div>
                    <div>
                      <p className="text-gray-700">Consider health conditions</p>
                      <p className="text-sm text-gray-500">Take into account any specific health conditions when determining food portions</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-gray-50 p-4 rounded-lg mb-8">
          <h2 className="text-xl font-semibold mb-4">Features & Benefits</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <h3 className="text-lg font-semibold mb-2">Key Features</h3>
              <div className="space-y-3">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center text-green-600">
                    <FaInfoCircle />
                  </div>
                  <div>
                    <h4 className="font-medium text-gray-700">Accurate Calculations</h4>
                    <p className="text-sm text-gray-500">Based on weight, age, and activity level for precise results</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center text-green-600">
                    <FaInfoCircle />
                  </div>
                  <div>
                    <h4 className="font-medium text-gray-700">Visual Analytics</h4>
                    <p className="text-sm text-gray-500">Interactive pie chart for easy nutritional breakdown understanding</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center text-green-600">
                    <FaInfoCircle />
                  </div>
                  <div>
                    <h4 className="font-medium text-gray-700">Life Stage Customization</h4>
                    <p className="text-sm text-gray-500">Specific calculations for puppies, adults, and seniors</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center text-green-600">
                    <FaInfoCircle />
                  </div>
                  <div>
                    <h4 className="font-medium text-gray-700">Activity Level Adjustment</h4>
                    <p className="text-sm text-gray-500">Tailored recommendations based on your dog's daily activity</p>
                  </div>
                </div>
              </div>
            </div>

            <div>
              <h3 className="text-lg font-semibold mb-2">Benefits</h3>
              <div className="space-y-3">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center text-green-600">
                    <FaInfoCircle />
                  </div>
                  <div>
                    <h4 className="font-medium text-gray-700">Prevent Overfeeding</h4>
                    <p className="text-sm text-gray-500">Get accurate portion sizes to maintain a healthy weight</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center text-green-600">
                    <FaInfoCircle />
                  </div>
                  <div>
                    <h4 className="font-medium text-gray-700">Optimal Nutrition</h4>
                    <p className="text-sm text-gray-500">Balanced macronutrients for your dog's specific life stage</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center text-green-600">
                    <FaInfoCircle />
                  </div>
                  <div>
                    <h4 className="font-medium text-gray-700">Weight Management</h4>
                    <p className="text-sm text-gray-500">Supports healthy weight maintenance through proper portion control</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center text-green-600">
                    <FaInfoCircle />
                  </div>
                  <div>
                    <h4 className="font-medium text-gray-700">Customizable Care</h4>
                    <p className="text-sm text-gray-500">Tailored recommendations for your dog's unique needs and lifestyle</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default DogFoodCalculator;
